import React, { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";

import AddUser from "./AddUser";

import { PrimaryButton } from "lib/components/Buttons/buttons";

import VisibilityIcon from "@mui/icons-material/Visibility";

import Search from "lib/components/Search/Search";
import { useUser } from "hooks/useUser";

import { PageLayout } from "lib";

import { Helmet } from "react-helmet";

import { UsersAPI } from "api_inbusiness";
import styles from "./Users.module.scss";
import UserCard from "./UserCard/UserCard";
import LoadingSpinner from "lib/components/Spinners/LoadingSpinner";

const Users = () => {
  const { permissionsData } = useUser();
  const navigate = useHistory();
  const usersPerPage = 12;
  const [currentPage, setCurrentPage] = React.useState(0);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerRefetch, setTriggerRefetch] = useState(false);
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [count, setCount] = useState(0);

  const resetSearch = () => {
    setIsSearch(false);
    setSearch("");
  };

  const getDefaultUsers = () => {
    // Always clear users and reset to page 0
    setUsers([]);
    setCurrentPage(0);
    setLoading(true);
    
    // Fetch first page
    UsersAPI.get(usersPerPage, 0, 1).then((res) => {
      if (res.ok && res.data?.content) {
        setUsers(res.data.content);
        setCount(res.data.count);
      } else {
        setUsers([]);
        setCount(0);
      }
      setLoading(false);
    });
  };

  const searchForUser = (searchText) => {
    UsersAPI.searchUsers(searchText).then((res) => {
      if (res.ok && Array.isArray(res.data)) {
        setUsers(res.data);
      } else {
        // In case of error or invalid data, set empty array
        setUsers([]);
      }
      setLoading(false);
    });
  };

  const getUsers = useCallback(() => {
    UsersAPI.get(usersPerPage, currentPage, 1).then((res) => {
      // If we're on page 0, replace the array instead of appending
      // This prevents duplicates when resetting from search
      if (currentPage === 0) {
        setUsers(res.data.content);
      } else {
        setUsers((prev) => [...prev, ...res.data.content]);
      }
      setCount(res.data.count);
      setLoading(false);
    });
  }, [currentPage]);

  // Initial load only - runs once on mount
  React.useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination - only when NOT in search mode
  React.useEffect(() => {
    if (!isSearch && currentPage > 0) {
      getUsers();
    }
  }, [currentPage, getUsers, isSearch]);

  const handleMoreUsers = () => {
    setCurrentPage((prev) => prev + 1);
  };

  return (
    <>
      <Helmet>
        <title>InBusiness CRM :: Membri</title>
      </Helmet>
      <PageLayout
        title={"Membri"}
        subTitle={"Administrează membri"}
        wide={true}
        actionArea={
          <>
            <Search
              isFilter={false}
              isSearch={isSearch}
              setIsSearch={setIsSearch}
              search={search}
              setSearch={setSearch}
              nameToFind={"Caută membru"}
              triggerRefetch={triggerRefetch}
              setTriggerRefetch={setTriggerRefetch}
              triggerSearch={triggerSearch}
              setTriggerSearch={setTriggerSearch}
              setLoading={setLoading}
              setterFunction={setUsers}
              setIsFilter={() => {}}
              resetFilter={resetSearch}
              defaultGet={getDefaultUsers}
              searchGet={searchForUser}
            />
            {permissionsData.seeAdminButtons && (
              <PrimaryButton
                startIcon={<VisibilityIcon />}
                color="primary"
                onClick={() => navigate.push("/users/admin/view")}
              >
                {"Vezi membri ca admin"}
              </PrimaryButton>
            )}
          </>
        }
      >
        {loading ? (
          <div className={styles.loadingWrapper}>
            <LoadingSpinner loading={loading} margin="1rem auto" />
          </div>
        ) : (
          <div className={styles.userCards}>
            {users.map((user) => (
              <UserCard user={user} key={user.id} />
            ))}
          </div>
        )}

        {!isSearch && count > users.length && (
          <div className={styles.loadMoreButton}>
            <PrimaryButton onClick={handleMoreUsers}>
              Încarcă mai mult
            </PrimaryButton>
          </div>
        )}
      </PageLayout>

      <AddUser
        open={open}
        setOpen={setOpen}
        triggerRefetch={triggerRefetch}
        setTriggerRefetch={setTriggerRefetch}
      />
    </>
  );
};

export default Users;
